/**
 * The bar over the booking grid: `Dziś`, the two steps, the range title, the legend, and the
 * Tydzień | Miesiąc switch — the A1 canon's row, left to right.
 *
 * Every control is a native `<button>`, so the whole bar is reachable with Tab and pressed with
 * Enter or Space.
 */

import { useTranslation } from 'react-i18next';

import segmented from '~/shared/ui/segmented.module.css';
import { ChevronGlyph } from '~/shared/ui/VideoGlyphs';

import type { BookingCalendarState } from '../lib/useBookingCalendar';
import { DayPlanGlyph } from './BookingGlyphs';
import { rangeTitle, type CalendarView } from '../model/calendarRange';
import styles from './CalendarToolbar.module.css';

const VIEWS: readonly CalendarView[] = ['week', 'month'];

interface CalendarToolbarProps {
  readonly state: BookingCalendarState;
}

export function CalendarToolbar({ state }: CalendarToolbarProps) {
  const { t, i18n } = useTranslation();
  const isWeek = state.view === 'week';
  const waiting = state.columnShown ? 0 : state.awaiting.length;

  return (
    <div className={styles.bar}>
      <div className={styles.pager}>
        {/* Always live, as the canon draws it: inside today's week it still brings the column
          * back to today. */}
        <button type="button" className={styles.today} onClick={state.goToday}>
          {t('schedule.calendar.today')}
        </button>
        <button
          type="button"
          className={styles.step}
          aria-label={t(isWeek ? 'calendar.weekNav.prev' : 'web:booking.toolbar.previousMonth')}
          onClick={state.goPrevious}
        >
          <ChevronGlyph className={styles.stepGlyph} direction="left" />
        </button>
        <button
          type="button"
          className={styles.step}
          aria-label={t(isWeek ? 'calendar.weekNav.next' : 'web:booking.toolbar.nextMonth')}
          onClick={state.goNext}
        >
          <ChevronGlyph className={styles.stepGlyph} direction="right" />
        </button>
        <div className={styles.rangeBox}>
          <h2 className={styles.range} aria-live="polite">
            {rangeTitle(state.view, state.anchor, i18n.language)}
          </h2>
          {/* One quiet line under the title, inside the controls' height: a failed refresh speaks
            * first, and whose hours these are otherwise. Neither moves the grid when it arrives. */}
          {state.stale ? (
            <p className={`${String(styles.zone)} ${String(styles.stale)}`} role="status">
              {t('web:booking.toolbar.stale')}
              <button type="button" className={styles.staleRetry} onClick={state.retry}>
                {t('calendar.retry')}
              </button>
            </p>
          ) : state.zoneNotice === null ? null : (
            <p className={styles.zone}>{t('web:booking.toolbar.zone', { zone: state.zoneNotice })}</p>
          )}
        </div>
      </div>

      <div className={styles.end}>
        <ul className={styles.legend} aria-label={t('web:booking.legend.label')}>
          <li className={styles.legendItem}>
            <i className={`${String(styles.swatch)} ${String(styles.swatchTraining)}`} aria-hidden="true" />
            {t('web:booking.legend.training')}
          </li>
          <li className={styles.legendItem}>
            <i className={`${String(styles.swatch)} ${String(styles.swatchConsultation)}`} aria-hidden="true" />
            {t('web:booking.legend.consultation')}
          </li>
          <li className={styles.legendItem}>
            <i className={`${String(styles.swatch)} ${String(styles.swatchClient)}`} aria-hidden="true" />
            {t('web:booking.legend.clientBooked')}
          </li>
          <li className={styles.legendItem}>
            <i className={`${String(styles.swatch)} ${String(styles.swatchOff)}`} aria-hidden="true" />
            {t('web:booking.legend.offHours')}
          </li>
        </ul>

        <div
          className={`${String(segmented.seg)} ${String(styles.views)}`}
          role="group"
          aria-label={t('web:booking.toolbar.view')}
        >
          {VIEWS.map((view) => (
            <button
              key={view}
              type="button"
              className={`${String(segmented.segOption)} ${
                view === state.view ? String(segmented.segOptionOn) : ''
              }`}
              aria-pressed={view === state.view}
              onClick={() => {
                state.setView(view);
              }}
            >
              {t(view === 'week' ? 'schedule.calendar.week' : 'web:booking.toolbar.month')}
            </button>
          ))}
        </div>

        {/* The day's plan, out on demand (owner, 25 September 2026). A disclosure: it names what a
          * press will do and says whether the column is out. While the column is away, what waits
          * for the trainer's verdict is counted on it — neutral ink, never a tinted fill. */}
        <button
          type="button"
          className={styles.planToggle}
          aria-expanded={state.columnShown}
          aria-controls="booking-day-plan"
          aria-label={[
            t(state.columnShown ? 'web:booking.toolbar.hidePlan' : 'web:booking.toolbar.showPlan'),
            waiting > 0 ? t('web:booking.toolbar.awaiting', { count: waiting }) : null,
          ]
            .filter(Boolean)
            .join(', ')}
          onClick={() => {
            if (state.openSession !== null) {
              state.closeSession();
              state.setPlanShown(false);
              return;
            }
            state.setPlanShown(!state.planShown);
          }}
        >
          <DayPlanGlyph className={styles.stepGlyph} />
          {waiting > 0 ? (
            <span className={styles.planBadge} aria-hidden="true">
              {waiting}
            </span>
          ) : null}
        </button>
      </div>
    </div>
  );
}
